import { DeepBoxError } from '@/models'
import DeepErrorHandlerService from '@/services/DeepErrorHandlerService.ts'
import { isHttpClientErrorOf } from '@deepcloud/api-http-client'
import { useDeepAdminUsersMeStore } from '@/stores/deepadmin/users/users-me.ts'
import { useApi } from '@/composables/api.ts'
import type {
  AuthorizationError,
  BoxInfo,
  ForbiddenError,
  GenericError,
  NotFoundError,
} from '@deepcloud/api-deep-box'

export const useDeepBoxBoxInfoStore = defineStore('deepBoxBoxInfo', () => {
  const { deepBoxAPI } = useApi()

  const boxInfo = ref<BoxInfo>()
  const fetchBoxInfoPending = ref(false)

  async function fetchBoxInfo(boxNodeId: string) {
    fetchBoxInfoPending.value = true
    try {
      const res = await deepBoxAPI.value.boxesInfo.get(boxNodeId)
      const resData = res.data
      boxInfo.value = { ...resData }

      return Promise.resolve(res)
    } catch (e: unknown) {
      console.error(e)
      let statusCode: number

      if (
        isHttpClientErrorOf<
          ForbiddenError | AuthorizationError | GenericError | NotFoundError
        >(e)
      ) {
        statusCode = e?.response?.status ?? 0

        if ([403, 404].includes(statusCode)) {
          const messageId = (e?.response?.data as ForbiddenError)?.messageId

          // the deepBoxAPI response interceptor already surfaces this one globally
          if (messageId !== 'elevated.auth.required') {
            DeepErrorHandlerService.error(
              new DeepBoxError('error.403.box_not_found_or_no_access', {
                statusCode,
              }),
            )
          }
        } else if (statusCode === 400) {
          DeepErrorHandlerService.error(
            new DeepBoxError('error.invalid_or_missing_params', {
              statusCode,
            }),
          )
        } else {
          DeepErrorHandlerService.error(
            new DeepBoxError('error.error_occurred', {
              statusCode,
            }),
          )
        }
      }

      return Promise.reject(e)
    } finally {
      fetchBoxInfoPending.value = false
    }
  }

  const deepAdminUsersMeStore = useDeepAdminUsersMeStore()
  const accessCompany = computed(() => {
    if (!boxInfo.value) return

    // private boxes have no accessGrantedBy entries; their org is boxInfo.company
    if (
      boxInfo.value.boxType === 'private' &&
      boxInfo.value.boxVariant === 'private'
    ) {
      return boxInfo.value.company
    }

    if (boxInfo.value.accessGrantedBy.length === 0) return

    const preferredOrganization = boxInfo.value.accessGrantedBy.find(
      (o) => o.companyId === deepAdminUsersMeStore.selectedOrganizationId,
    )

    return preferredOrganization || boxInfo.value.accessGrantedBy[0]
  })

  // a private box's org is never "selected", so its beta_features would otherwise never be fetched
  watch(
    () => accessCompany.value?.companyId,
    (companyId) => {
      if (companyId) {
        void deepAdminUsersMeStore.fetchOrgBetaFeatures(companyId)
      }
    },
  )

  const isOwnedBox = computed(() => {
    if (!boxInfo.value?.company) return false

    const accessGrantedByCompanyIds = boxInfo.value.accessGrantedBy.map(
      (c) => c.companyId,
    )

    return accessGrantedByCompanyIds.includes(boxInfo.value?.company?.companyId)
  })

  async function updateBoxInfoQueueCounterByAPI(typeId: string, boxId: string) {
    if (!boxInfo.value) return
    // this fn should update the queueCount prop inside "boxInfo" in the store: deepBoxBoxInfoStore
    // we use the API to make the call faster as "re-fetch" the boxInfo
    const res = await deepBoxAPI.value.deepBoxesBoxesQueue.get(typeId, boxId, {
      // the limit = 0 is to make the API faster as we only need the "size" from the response
      limit: 0,
    })

    boxInfo.value = {
      ...boxInfo.value,
      queueCount: res.data?.size ?? 0,
    }
  }

  function increaseBoxInfoQueueCounter(count = 1) {
    if (!boxInfo.value) return
    boxInfo.value = {
      ...boxInfo.value,
      queueCount: boxInfo.value.queueCount + count,
    }
  }

  function decreaseBoxInfoQueueCounter(count = 1) {
    if (!boxInfo.value) return
    boxInfo.value = {
      ...boxInfo.value,
      queueCount: boxInfo.value.queueCount - count,
    }
  }

  return {
    // state
    boxInfo,
    fetchBoxInfoPending,
    accessCompany,
    isOwnedBox,
    // actions
    fetchBoxInfo,

    updateBoxInfoQueueCounterByAPI,
    increaseBoxInfoQueueCounter,
    decreaseBoxInfoQueueCounter,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useDeepBoxBoxInfoStore, import.meta.hot),
  )
}
